import { Avatar } from '@creditornot/cb-avatar';
import { IconButton } from '@creditornot/cb-button';
import { Pencil } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import { Modal, ModalContent, ModalHeader } from '@creditornot/cbt-modal';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography } from 'common/typography';
import { useExperienceModuleContext } from 'modules/experience/context';

import { Search } from '../Search/Search';

const EntitySwitcherContainer = styled.div<{ isSidebarOpen: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  align-self: stretch;
  height: 120px;
  gap: 8px;
  background-color: ${({ isSidebarOpen }) => (isSidebarOpen ? cbSemanticCssVars.surfaceMain : 'transparent')};
  margin: 16px;
  padding: ${({ isSidebarOpen }) => (isSidebarOpen ? '16px' : 'initial')};
  padding-block-start: 16px;
`;

const EntityAvatar = styled(Avatar)`
  cursor: pointer;
  width: 36px;
  height: 36px;

  img {
    object-fit: cover;
  }
`;

const EntityTitle = styled(Typography.Title2)`
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EntityDescription = styled.div<{ isSidebarOpen: boolean }>`
  display: ${({ isSidebarOpen }) => (isSidebarOpen ? 'flex' : 'none')};
  flex-direction: column;
  justify-content: flex-start;
  gap: 4px;
  align-self: stretch;
  height: 44px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
`;
const EntityDescriptionPopover = styled(Typography.Small)`
  display: block;
  max-width: 200px;
`;

const EntityLocation = styled(Typography.Small)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const EntityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  height: 36px;
`;

export const EntitySwitcher = ({ isSidebarOpen }: { isSidebarOpen: boolean }) => {
  const { name, image, initials, address, city } = useExperienceModuleContext();
  const [isEntitySwitcherModalOpen, setIsEntitySwitcherModalOpen] = useState(false);

  return (
    <>
      <EntitySwitcherContainer isSidebarOpen={isSidebarOpen}>
        <EntityHeader>
          <EntityAvatar
            size="small"
            imgSrc={image}
            initials={initials}
            onClick={() => setIsEntitySwitcherModalOpen(true)}
          />
          {isSidebarOpen && (
            <IconButton variant="blue" size="tiny" onClick={() => setIsEntitySwitcherModalOpen(true)}>
              <Pencil />
            </IconButton>
          )}
        </EntityHeader>
        <Popover
          content={
            <EntityDescriptionPopover>
              {name}
              {address && `, ${address}`}
              {city && `, ${city}`}
            </EntityDescriptionPopover>
          }
          showOnHover={false}
          showOnClick
        >
          <EntityDescription isSidebarOpen={isSidebarOpen}>
            <EntityTitle>{name}</EntityTitle>
            <EntityLocation>
              {address}
              {city && ', '} {city}
            </EntityLocation>
          </EntityDescription>
        </Popover>
      </EntitySwitcherContainer>
      {isEntitySwitcherModalOpen && (
        <Modal
          open={isEntitySwitcherModalOpen}
          onDismiss={() => setIsEntitySwitcherModalOpen(false)}
          disableContentPadding
          sizePreset="centered:large"
        >
          <ModalHeader onCloseClick={() => setIsEntitySwitcherModalOpen(false)}>
            <FormattedMessage id="experience.search.description" />
          </ModalHeader>
          <ModalContent>
            <Search onSelect={() => setIsEntitySwitcherModalOpen(false)} />
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
